import getExclusionReason from '@app.raytd.com/common/utils/tests/getExclusionReason';
import { toast } from 'sonner';
import { Assessment } from '../assessment/assessment.slice';
import { AssetEntity } from '../asset';
import { TestCase, TestItem, TestSuite, TestSuiteView } from '../tests/entity';
import type { ReferenceImage, ReferenceImageDisposition } from '../tests/api/referenceImages';
import { TestSpecificationsEntity } from '../tests/test-specifications.slice';
import { UsersEntity } from '../users';
import { transformClassificationTree } from './elementTransformer';
import {
  ActivityItem,
  AddCustomFieldOptionRequest,
  AddCustomFieldOptionResponse,
  ClassificationItem,
  DashboardRecentResponse,
  FastFillUpdateRequest,
  GetTestItemRequest,
  PublishedReportResponse,
  PublishReportPayload,
  PublishReportResponse,
  ReportBlock,
  ReportEntity,
  ReportPayload,
  ReportResponse,
  ReportRevision,
  ReportSection,
  ReportTestItem,
  ReportUpdatePayload,
  ToggleSwitchRequest,
  ToggleSwitchResponse,
  TestItemComment,
} from './types';
import updateItemInReportCache from './updateItemInReportCache';
import {
  NotificationPagination,
  AppNotification,
  ReportExport,
} from '../notifications/types';
import { rAPI } from './rAPI';
import { inspectionsReportsAPI } from './inspectionsReportsAPI';

export const testSuitesAPI = rAPI.injectEndpoints({
  overrideExisting: false,
  endpoints: (builder) => ({
    getTestSuites: builder.query<
      TestSuite[],
      { with_specifications?: boolean }
    >({
      query: ({ with_specifications = false } = {}) =>
        'test-suites?with_specifications=' + with_specifications,
      transformResponse: (response: { data: any[] }) =>
        response.data.map((suite: any) => ({
          ...suite,
          inspections: suite.total_assessments,
          categories: suite.total_categories,
          subCategories: suite.total_specifications,
          tests: suite.total_specifications,
          // status: suite.status === 'inuse' ? 'active' : 'archived',
        })),
      providesTags: (result) =>
        result
          ? [
              // Provide a tag for the entire list
              { type: 'TestSuites', id: 'LIST' },
              // Provide individual tags for each test suite
              ...result.map((suite) => ({
                type: 'TestSuites' as const,
                id: suite.id,
              })),
              // If the suites include specifications, tag them too
              ...result.flatMap(
                (suite) =>
                  suite.specifications?.map((spec: TestCase) => ({
                    type: 'Specifications' as const,
                    id: spec.id,
                  })) || []
              ),
            ]
          : [{ type: 'TestSuites', id: 'LIST' }],
    }),

    getTestSuite: builder.query({
      query: (testSuiteId) => 'test-suites/' + testSuiteId,
      transformResponse: (response: { data: any }) => {
        const suite = response.data;
        return {
          ...suite,
          inspections: suite.total_assessments,
          categories: suite.total_categories,
          subCategories: suite.total_specifications,
          tests: suite.total_specifications,
          //status: suite.status === 'archived' ? 'archived' : 'active',
        };
      },
      providesTags: (result, error, id) =>
        result
          ? [
              // Tag for the specific test suite
              { type: 'TestSuites', id },
              // Tag for the list of test suites
              { type: 'TestSuites', id: 'LIST' },
              // Tags for specifications if they exist
              ...(result.specifications?.map((spec) => ({
                type: 'Specifications' as const,
                id: spec.id,
              })) || []),
              // Tag for the specifications list of this suite
              { type: 'Specifications', id: `${id}-LIST` },
            ]
          : [
              { type: 'TestSuites', id },
              { type: 'TestSuites', id: 'LIST' },
            ],
    }),

    getMultipleTestSuites: builder.query<TestSuite[], number[]>({
      queryFn: async (ids, api, extraOptions, baseQuery) => {
        try {
          const requests = ids.map(async (id) => {
            const result = await baseQuery(`test-suites/${id}`);
            if (result.error) {
              throw result.error;
            }
            return (result.data as { data: TestSuite }).data;
          });

          const results = await Promise.all(requests);
          return { data: results };
        } catch (error) {
          return { error: error as any };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map((suite) => ({
                type: 'TestSuites' as const,
                id: suite.id,
              })),
              { type: 'TestSuites', id: 'LIST' },
            ]
          : [{ type: 'TestSuites', id: 'LIST' }],

      // Optional: Add merge strategy for concurrent requests
      merge: (currentCache, newItems) => {
        // Merge logic if needed
        return [...newItems];
      },

      // Optional: Add serialization strategy for cache key
      serializeQueryArgs: ({ queryArgs }) => {
        return queryArgs.sort().join(',');
      },
    }),

    createTestSuite: builder.mutation({
      query: (newTestSuite) => ({
        url: 'test-suites',
        method: 'POST',
        body: newTestSuite,
      }),
      invalidatesTags: [{ type: 'TestSuites', id: 'LIST' }],
    }),

    updateTestSuite: builder.mutation({
      query: ({ id, ...testSuite }) => ({
        url: `test-suites/${id}`,
        method: 'PUT',
        body: testSuite,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'TestSuites', id },
        { type: 'TestSuites', id: 'LIST' },
      ],
    }),

    updateTestSuiteFields: builder.mutation({
      query: ({ id, ...fields }) => ({
        url: `test-suites/${id}/fields`,
        method: 'PUT',
        body: fields,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'TestSuites', id },
        { type: 'TestSuites', id: 'LIST' },
      ],
    }),

    duplicateTestSuite: builder.mutation({
      query: (id) => ({
        url: `test-suites/${id}/duplicate`,
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'TestSuites', id: 'LIST' }],
    }),

    archiveTestSuiteForUser: builder.mutation({
      query: (id) => ({
        url: `test-suites/${id}/status`,
        method: 'PUT',
      }),
      invalidatesTags: ['TestSuites'],
    }),

    archiveTestSuite: builder.mutation({
      query: (id) => ({
        url: `test-suites/${id}/archive`,
        method: 'PUT',
      }),
      invalidatesTags: ['TestSuites'],
    }),

    getTestSpecifications: builder.query<
      TestCase[],
      number | string | { testSuiteId: number | string; viewId?: number | null }
    >({
      query: (arg) => {
        const testSuiteId = typeof arg === 'object' ? arg.testSuiteId : arg;
        const viewId = typeof arg === 'object' ? arg.viewId : undefined;
        return `test-suites/${testSuiteId}/tests${viewId ? `?view_id=${viewId}` : ''}`;
      },
      transformResponse: (response: { data: any[] }) => response.data,
      // providesTags: (result) =>
      //   result
      //     ? [
      //         ...result.map((spec) => ({
      //           type: 'Specifications' as const,
      //           id: spec.id,
      //         })),
      //         { type: 'Specifications', id: 'LIST' },
      //       ]
      //     : [{ type: 'Specifications', id: 'LIST' }],
      providesTags: (result) => [
        ...(result?.map((spec) => ({
          type: 'Specifications' as const,
          id: spec.id,
        })) || []),
        { type: 'Specifications', id: 'LIST' },
        { type: 'TestSuites', id: 'LIST' },
      ],
    }),

    updateTestSpecification: builder.mutation<
      TestSpecificationsEntity,
      {
        testSuiteId: number;
        testSpecificationId: number;
        test: Partial<TestSpecificationsEntity>;
      }
    >({
      query: ({ testSuiteId, testSpecificationId, test }) => ({
        url: `test-suites/${testSuiteId}/tests/${testSpecificationId}`,
        method: 'PUT',
        body: test,
      }),
      invalidatesTags: (
        result,
        error,
        { testSuiteId, testSpecificationId }
      ) => [{ type: 'Specifications', id: testSpecificationId }],
    }),

    createTestSpecification: builder.mutation<
      TestSpecificationsEntity,
      { testSuiteId: number; test: Partial<TestSpecificationsEntity> }
    >({
      query: ({ testSuiteId, test }) => ({
        url: `test-suites/${testSuiteId}/tests`,
        method: 'POST',
        body: test,
      }),
      invalidatesTags: [{ type: 'Specifications', id: 'LIST' }],
    }),

    deleteTestSpecification: builder.mutation<
      void,
      { testSuiteId: number; testSpecificationId: number }
    >({
      query: ({ testSuiteId, testSpecificationId }) => ({
        url: `test-suites/${testSuiteId}/tests/${testSpecificationId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Specifications', id: 'LIST' }],
    }),

    renameTestSpecificationCategory: builder.mutation<
      void,
      {
        testSuiteId: string;
        new_category: string;
        old_category: string;
      }
    >({
      query: ({ testSuiteId, new_category, old_category }) => ({
        url: `test-suites/${testSuiteId}/categories`,
        method: 'PUT',
        body: { new_category, old_category },
      }),
      invalidatesTags: ['Specifications'],
    }),

    renameTestSpecificationSubCategory: builder.mutation<
      void,
      {
        testSuiteId: string;
        category: string;
        old_subcategory: string;
        new_subcategory: string;
      }
    >({
      query: ({ testSuiteId, category, old_subcategory, new_subcategory }) => ({
        url: `test-suites/${testSuiteId}/subcategories`,
        method: 'PUT',
        body: { category, old_subcategory, new_subcategory },
      }),
      invalidatesTags: ['Specifications'],
    }),

    addTestSpecificationFastFill: builder.mutation<
      any,
      { testSpecificationId: number; data: FastFillUpdateRequest }
    >({
      query: ({ testSpecificationId, data }) => ({
        url: `/test-suites/tests/${testSpecificationId}/fast-fill`,
        method: 'POST',
        body: data,
      }),
      async onQueryStarted(
        { testSpecificationId, data },
        { dispatch, queryFulfilled }
      ) {
        // Get the current cache key for the test specifications
        const patchResult = dispatch(
          testSuitesAPI.util.updateQueryData(
            'getTestSpecifications',
            testSpecificationId,
            (draft) => {
              const specification = draft.find(
                (spec) => spec.id === testSpecificationId
              );
              if (specification) {
                //we need to update the relevant observations or recommendations
                if (data.type === 'observations') {
                  specification.observations = [
                    ...specification.observations,
                    data,
                  ];
                } else if (data.type === 'recommendations') {
                  specification.recommendations = [
                    ...specification.recommendations,
                    data,
                  ];
                }
              }
            }
          )
        );

        try {
          await queryFulfilled;
        } catch {
          // Undo the optimistic update on error
          patchResult.undo();
        }
      },
      // Optionally invalidate related queries after the mutation
      invalidatesTags: (result, error, { testSpecificationId }) => [
        { type: 'Specifications', id: testSpecificationId },
      ],
    }),

    getReferenceImages: builder.query<
      ReferenceImage[],
      { testSuiteId: number; testSpecificationId: number }
    >({
      query: ({ testSuiteId, testSpecificationId }) =>
        `test-suites/${testSuiteId}/tests/${testSpecificationId}/reference-images`,
      transformResponse: (response: { data: ReferenceImage[] } | ReferenceImage[]) =>
        Array.isArray(response) ? response : (response?.data ?? []),
      providesTags: (result, error, { testSpecificationId }) => [
        ...(result?.map((img) => ({ type: 'ReferenceImages' as const, id: img.id })) ?? []),
        { type: 'ReferenceImages', id: `LIST-${testSpecificationId}` },
      ],
    }),

    uploadReferenceImage: builder.mutation<
      ReferenceImage,
      {
        testSuiteId: number;
        testSpecificationId: number;
        file: File;
        disposition?: ReferenceImageDisposition;
        displayName?: string;
      }
    >({
      query: ({ testSuiteId, testSpecificationId, file, disposition, displayName }) => {
        const form = new FormData();
        form.append('file', file);
        if (disposition) {
          form.append('disposition', disposition);
        }
        if (displayName) {
          form.append('display_name', displayName);
        }
        return {
          url: `test-suites/${testSuiteId}/tests/${testSpecificationId}/reference-images`,
          method: 'POST',
          body: form,
        };
      },
      invalidatesTags: (result, error, { testSpecificationId }) => [
        { type: 'ReferenceImages', id: `LIST-${testSpecificationId}` },
      ],
    }),

    updateReferenceImageDisposition: builder.mutation<
      ReferenceImage,
      {
        testSuiteId: number;
        testSpecificationId: number;
        referenceImageId: number;
        disposition: ReferenceImageDisposition;
      }
    >({
      query: ({ testSuiteId, testSpecificationId, referenceImageId, disposition }) => ({
        url: `test-suites/${testSuiteId}/tests/${testSpecificationId}/reference-images/${referenceImageId}`,
        method: 'PUT',
        body: { disposition },
      }),
      async onQueryStarted(
        { testSuiteId, testSpecificationId, referenceImageId, disposition },
        { dispatch, queryFulfilled },
      ) {
        const patch = dispatch(
          testSuitesAPI.util.updateQueryData(
            'getReferenceImages',
            { testSuiteId, testSpecificationId },
            (draft) => {
              const image = draft.find((img) => img.id === referenceImageId);
              if (image) {
                image.disposition = disposition;
              }
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
          toast.error('Failed to update disposition');
        }
      },
    }),

    updateReferenceImageDisplayName: builder.mutation<
      ReferenceImage,
      {
        testSuiteId: number;
        testSpecificationId: number;
        referenceImageId: number;
        disposition: ReferenceImageDisposition;
        displayName: string;
      }
    >({
      query: ({ testSuiteId, testSpecificationId, referenceImageId, disposition, displayName }) => ({
        url: `test-suites/${testSuiteId}/tests/${testSpecificationId}/reference-images/${referenceImageId}`,
        method: 'PUT',
        body: { disposition, display_name: displayName },
      }),
      async onQueryStarted(
        { testSuiteId, testSpecificationId, referenceImageId, displayName },
        { dispatch, queryFulfilled },
      ) {
        const patch = dispatch(
          testSuitesAPI.util.updateQueryData(
            'getReferenceImages',
            { testSuiteId, testSpecificationId },
            (draft) => {
              const image = draft.find((img) => img.id === referenceImageId);
              if (image) {
                image.display_name = displayName || null;
              }
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
          toast.error('Failed to update image title');
        }
      },
    }),

    archiveReferenceImage: builder.mutation<
      void,
      { testSuiteId: number; testSpecificationId: number; referenceImageId: number }
    >({
      query: ({ testSuiteId, testSpecificationId, referenceImageId }) => ({
        url: `test-suites/${testSuiteId}/tests/${testSpecificationId}/reference-images/${referenceImageId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { testSpecificationId }) => [
        { type: 'ReferenceImages', id: `LIST-${testSpecificationId}` },
      ],
    }),

    getRatingScales: builder.query({
      query: () => 'rating-scales',
      transformResponse: (response: { data: any[] }) => response.data,
      providesTags: ['RatingScales'],
    }),

    updateRatingScale: builder.mutation({
      query: ({ id, ...ratingScale }) => ({
        url: `rating-scales/${id}`,
        method: 'PUT',
        body: ratingScale,
      }),
      invalidatesTags: ['RatingScales'],
    }),

    addCustomFieldOption: builder.mutation<
      AddCustomFieldOptionResponse,
      AddCustomFieldOptionRequest
    >({
      query: ({ testSuiteId, ...body }) => ({
        url: `test-suites/${testSuiteId}/add-field-option`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { testSuiteId }) => [
        { type: 'TestSuites', id: testSuiteId },
      ],
      async onQueryStarted(
        { testSuiteId, reportId, ...optionData },
        { dispatch, queryFulfilled }
      ) {
        const undoActions: (() => void)[] = [];

        const detailPatch = dispatch(
          testSuitesAPI.util.updateQueryData('getTestSuite', testSuiteId, (draft) => {
            const fieldToUpdate = draft.custom_fields.find(
              (f) => f.field_id === optionData.field_id
            );
            if (fieldToUpdate) {
              if (!fieldToUpdate.data) {
                fieldToUpdate.data = [];
              }
              fieldToUpdate.data.push({
                id: optionData.id,
                displayName: optionData.displayName,
              });
            }
          })
        );
        undoActions.push(detailPatch.undo);

        if (reportId) {
          const listPatch = dispatch(
            inspectionsReportsAPI.util.updateQueryData(
              'getReportTestSuites',
              reportId,
              (draft) => {
                const fieldToUpdate = draft
                  .find((s) => s.id === testSuiteId)
                  ?.custom_fields.find(
                    (f) => f.field_id === optionData.field_id
                  );
                if (fieldToUpdate) {
                  if (!fieldToUpdate.data) {
                    fieldToUpdate.data = [];
                  }
                  fieldToUpdate.data.push({
                    id: optionData.id,
                    displayName: optionData.displayName,
                  });
                }
              }
            )
          );
          undoActions.push(listPatch.undo);
        }

        try {
          await queryFulfilled;
        } catch {
          undoActions.forEach((undo) => undo());
        }
      },
    }),

    // --- Test Suite Views (ADR-0008) ---

    getTestSuiteViews: builder.query<TestSuiteView[], number>({
      query: (testSuiteId) => `test-suites/${testSuiteId}/views`,
      transformResponse: (response: { data: TestSuiteView[] }) => response.data,
      providesTags: (result, error, testSuiteId) => [
        ...(result?.map((view) => ({ type: 'TestSuiteViews' as const, id: view.id })) ?? []),
        { type: 'TestSuiteViews', id: `LIST-${testSuiteId}` },
      ],
    }),

    createTestSuiteView: builder.mutation<
      TestSuiteView,
      { testSuiteId: number; name: string; description?: string }
    >({
      query: ({ testSuiteId, ...body }) => ({
        url: `test-suites/${testSuiteId}/views`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { testSuiteId }) => [
        { type: 'TestSuiteViews', id: `LIST-${testSuiteId}` },
      ],
    }),

    updateTestSuiteView: builder.mutation<
      TestSuiteView,
      { testSuiteId: number; viewId: number; name?: string; description?: string; retired?: boolean }
    >({
      query: ({ testSuiteId, viewId, ...body }) => ({
        url: `test-suites/${testSuiteId}/views/${viewId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result, error, { testSuiteId, viewId }) => [
        { type: 'TestSuiteViews', id: viewId },
        { type: 'TestSuiteViews', id: `LIST-${testSuiteId}` },
      ],
    }),

    reorderTestSuiteViews: builder.mutation<
      TestSuiteView[],
      { testSuiteId: number; viewIds: number[] }
    >({
      query: ({ testSuiteId, viewIds }) => ({
        url: `test-suites/${testSuiteId}/views/reorder`,
        method: 'PUT',
        body: { view_ids: viewIds },
      }),
      invalidatesTags: (result, error, { testSuiteId }) => [
        { type: 'TestSuiteViews', id: `LIST-${testSuiteId}` },
      ],
    }),

    deleteTestSuiteView: builder.mutation<void, { testSuiteId: number; viewId: number }>({
      query: ({ testSuiteId, viewId }) => ({
        url: `test-suites/${testSuiteId}/views/${viewId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { testSuiteId }) => [
        { type: 'TestSuiteViews', id: `LIST-${testSuiteId}` },
      ],
    }),

    // --- View membership (inclusion pivot) ---

    getTestSpecificationViews: builder.query<
      { test_specification_id: number; view_ids: number[] },
      { testSuiteId: number; testSpecificationId: number }
    >({
      query: ({ testSuiteId, testSpecificationId }) =>
        `test-suites/${testSuiteId}/tests/${testSpecificationId}/views`,
      providesTags: (result, error, { testSpecificationId }) => [
        { type: 'TestSpecificationViews', id: testSpecificationId },
      ],
    }),

    updateTestSpecificationViews: builder.mutation<
      { test_specification_id: number; view_ids: number[] },
      { testSuiteId: number; testSpecificationId: number; viewIds: number[] }
    >({
      query: ({ testSuiteId, testSpecificationId, viewIds }) => ({
        url: `test-suites/${testSuiteId}/tests/${testSpecificationId}/views`,
        method: 'PUT',
        body: { view_ids: viewIds },
      }),
      invalidatesTags: (result, error, { testSuiteId, testSpecificationId }) => [
        { type: 'TestSpecificationViews', id: testSpecificationId },
        { type: 'TestSuiteViews', id: `LIST-${testSuiteId}` },
      ],
    }),

    getTestSuiteViewMatrix: builder.query<
      { memberships: Record<number, number[]> },
      number
    >({
      query: (testSuiteId) => `test-suites/${testSuiteId}/views-matrix`,
      providesTags: (result, error, testSuiteId) => [
        { type: 'TestSuiteViews', id: `LIST-${testSuiteId}` },
        { type: 'TestSpecificationViews', id: 'LIST' },
      ],
    }),

    saveTestSuiteViewMatrix: builder.mutation<
      { message: string },
      { testSuiteId: number; memberships: Record<number, number[]> }
    >({
      query: ({ testSuiteId, memberships }) => ({
        url: `test-suites/${testSuiteId}/views-matrix`,
        method: 'PUT',
        body: { memberships },
      }),
      invalidatesTags: (result, error, { testSuiteId }) => [
        { type: 'TestSuiteViews', id: `LIST-${testSuiteId}` },
        'Specifications',
        { type: 'TestSpecificationViews' as const, id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useAddCustomFieldOptionMutation,
  useGetTestSuitesQuery,
  useGetTestSuiteQuery,
  useGetMultipleTestSuitesQuery,
  useCreateTestSuiteMutation,
  useUpdateTestSuiteMutation,
  useUpdateTestSuiteFieldsMutation,
  useArchiveTestSuiteForUserMutation,
  useArchiveTestSuiteMutation,
  useDuplicateTestSuiteMutation,
  useGetTestSpecificationsQuery,
  useCreateTestSpecificationMutation,
  useUpdateTestSpecificationMutation,
  useDeleteTestSpecificationMutation,
  useRenameTestSpecificationCategoryMutation,
  useRenameTestSpecificationSubCategoryMutation,
  useAddTestSpecificationFastFillMutation,
  useGetReferenceImagesQuery,
  useUploadReferenceImageMutation,
  useUpdateReferenceImageDispositionMutation,
  useUpdateReferenceImageDisplayNameMutation,
  useArchiveReferenceImageMutation,
  useGetRatingScalesQuery,
  useUpdateRatingScaleMutation,
  useGetTestSuiteViewsQuery,
  useCreateTestSuiteViewMutation,
  useUpdateTestSuiteViewMutation,
  useReorderTestSuiteViewsMutation,
  useDeleteTestSuiteViewMutation,
  useGetTestSpecificationViewsQuery,
  useUpdateTestSpecificationViewsMutation,
  useGetTestSuiteViewMatrixQuery,
  useSaveTestSuiteViewMatrixMutation,
} = testSuitesAPI;
